import React, { useState, useEffect } from 'react';
import { Bell, X, ExternalLink, Sparkles, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PublicationNotificationData } from '../services/notificationService';

export const PublicationNotificationToast: React.FC = () => {
  const [currentNotification, setCurrentNotification] = useState<PublicationNotificationData | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleNotification = (event: Event) => {
      const customEvent = event as CustomEvent<PublicationNotificationData>;
      if (customEvent.detail) {
        setCurrentNotification(customEvent.detail);
      }
    };

    window.addEventListener('literafan:publication_notification', handleNotification);
    return () => {
      window.removeEventListener('literafan:publication_notification', handleNotification);
    };
  }, []);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (!currentNotification) return;

    const timer = setTimeout(() => {
      setCurrentNotification(null);
    }, 8000);

    return () => clearTimeout(timer);
  }, [currentNotification]);

  if (!currentNotification) return null;

  const handleClickAction = () => {
    if (currentNotification.url) {
      if (currentNotification.url.startsWith('/#')) {
        const path = currentNotification.url.replace('/#', '');
        navigate(path);
      } else if (currentNotification.url.startsWith('#')) {
        const path = currentNotification.url.replace('#', '');
        navigate(path);
      } else {
        navigate(currentNotification.url);
      }
    }
    setCurrentNotification(null);
  };

  return (
    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[150] w-full max-w-md px-4 pointer-events-none font-sans">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -25, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="pointer-events-auto bg-[#121212]/95 border border-primary/40 rounded-2xl p-4 shadow-[0_15px_40px_rgba(0,0,0,0.8),0_0_20px_rgba(193,18,31,0.2)] backdrop-blur-xl text-stone-200 relative overflow-hidden"
        >
          {/* Top accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-red-500 to-rose-600 animate-pulse" />

          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-primary flex-shrink-0 shadow-[0_0_12px_rgba(193,18,31,0.3)]">
              {currentNotification.type === 'article' ? (
                <BookOpen size={18} className="animate-bounce" />
              ) : currentNotification.type === 'welcome' ? (
                <Sparkles size={18} className="text-primary animate-pulse" />
              ) : (
                <Bell size={18} />
              )}
            </div>

            <div className="flex-grow pr-6">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] uppercase font-bold tracking-wider text-primary">
                  {currentNotification.type === 'welcome' ? 'Inscrição Ativa' : 'Nova Publicação'}
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
              </div>

              <h4 className="font-serif font-bold text-stone-100 text-sm leading-snug line-clamp-2">
                {currentNotification.title}
              </h4>
              
              <p className="text-xs text-stone-400 mt-1 line-clamp-2 leading-relaxed">
                {currentNotification.body}
              </p>

              {currentNotification.url && (
                <button
                  onClick={handleClickAction}
                  className="mt-2.5 inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-primary hover:bg-accent text-white rounded-full text-xs font-bold transition-all shadow-md active:scale-95"
                >
                  <span>Conferir Agora</span>
                  <ExternalLink size={12} />
                </button>
              )}
            </div>

            <button
              onClick={() => setCurrentNotification(null)}
              className="absolute top-3 right-3 p-1.5 rounded-full text-stone-500 hover:text-stone-300 hover:bg-stone-800/60 transition-colors"
              title="Dispensar aviso"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default PublicationNotificationToast;
